import {
    getDefaultCategories,
    type MappedProductVariant,
    type ProductCategories,
    type ProductHit,
    type SelectedVariants,
} from '@libs/algolia';
import { formatAutoshipFrequency, type ItemsListItem, NavigationListTypes, type OcapiProps } from '@libs/common-next';
import type { StoreV2 } from '@libs/common-types';
import { Currency, getLocale, Locale } from '@libs/core';

import { TrackingParams } from '../constants/trackingParams';
import { TrackingEvents } from '../enums';
import type { TrackCartItem } from '../gtm/events';
import { getGa4ItemId } from '../gtm/getGa4ItemId';
import { toDollarsAmount } from '../gtm/helpers';
import type { EventItem } from '../types';
import { buildFulfillmentMethodLabel } from './buildFulfillmentMethodLabel';
import { getNavigationId, getNavigationName, processFindingMethod } from './ga4ItemListAttributionFromSkuFindingMethod';
import { resolveGa4ItemListAttributionForCartItem } from './resolveGa4ItemListAttributionForCartItem';
import { formatScheduledDeliveryDate } from './scheduledDeliveryDateUtils';
import { getSkuSearchTerm, resolveItemSearchTerm } from './skuSearchTermStorage';
import { getSearchStats } from './storageSearch';

type CategoryInfo = {
    primaryCategoryId: number | null;
    categories: ProductCategories | null;
};

type AnalyticsInput = {
    product: MappedProductVariant;
    ocapi: OcapiProps | null;
    variant?: SelectedVariants | null;
    categoryInfo: CategoryInfo;
    listInfo: {
        id: string | null;
        name: string | null;
    };
    isDefaultProduct: boolean;
    lastSearchQuery?: string | null;
    eventType?: string;
};

export const getItemCategories = (product: MappedProductVariant | undefined) => ({
    item_category: product?.merchandisingHierarchy?.vicePresidentName ?? '',
    item_category2: product?.merchandisingHierarchy?.division ?? '',
    item_category3: product?.merchandisingHierarchy?.department ?? '',
    item_category4: product?.merchandisingHierarchy?.class ?? '',
    item_category5: product?.merchandisingHierarchy?.category ?? '',
});

export const getItemInfo = <T extends AnalyticsInput>(input: T) => ({
    price: input.product?.price?.number ?? 0,
    quantity: input.ocapi?.basket?.order_total || 1,
    color: input.product?.variants?.color?.display ?? '',
    default_version: input.isDefaultProduct ? 'yes' : 'no',
    size: input.product?.variants?.size?.display ?? '',
    flavor: input.product?.flavor?.value ?? '',
    ...(input.eventType !== TrackingEvents.VIEW_ITEM.toString()
        ? { pharmacy_quantity: input.ocapi?.basket?.order_total || 1 }
        : {}),
    item_list_id: input.listInfo?.id ?? '',
    item_list_name: input.listInfo?.name ?? '',
    item_brand: input.product.brand ?? '',
    item_id: input.product.masterProductID?.toString() ?? '',
    item_name: input.product.name ?? '',
    item_variant: input.product.id.toString() ?? '',
    item_search_term: input.listInfo?.id === NavigationListTypes.SEARCH ? (input?.lastSearchQuery ?? '') : null,
    ...getItemCategories(input.product),
});

export const getEcommerceInfo = ({
    product,
    variant,
    categoryInfo,
    listInfo,
    ocapi,
    isDefaultProduct,
    lastSearchQuery,
    eventType,
}: AnalyticsInput) => {
    const categories = getDefaultCategories(product as ProductHit);
    const locale = getLocale();

    const currency = locale === Locale['en-CA'] ? Currency.CAD : Currency.USD;

    return {
        currency,
        value: (ocapi?.basket?.order_total || 1) * Number(product?.price?.number ?? 0),
        pdp_type: NavigationListTypes.STANDARD_PDP,
        ...(eventType !== TrackingEvents.VIEW_ITEM.toString() ? { product_recommendation: 'no' } : {}),
        category: categories[0]?.name?.default ?? '',
        sub_category1: categories[1]?.name?.default ?? '',
        sub_category2: categories[2]?.name?.default ?? '',
        items: [
            getItemInfo({
                product,
                ocapi,
                variant,
                listInfo,
                categoryInfo,
                isDefaultProduct,
                lastSearchQuery,
                eventType,
            }),
        ],
    };
};

export const mapFBTAddToCartItems = (
    product: ProductHit,
    products: Array<ProductHit & { isSelected: boolean }>,
    store: StoreV2 | null | undefined,
    itemCartOrder: number,
    selectedDeliveryMethod: string,
    lastSearchQuery: string,
    autoShipFrequency?: number | undefined
): EventItem[] => {
    const findingMethod = processFindingMethod(window.sessionStorage.pdpFindingMethod);
    const isFromSearch = findingMethod === TrackingParams.SEARCH_REFERER;

    return products.map(item => ({
        item_name: item.name,
        item_id: item.masterProductID?.toString(),
        quantity: 1,
        price: item.price?.number,
        ...getItemCategories(item),
        item_brand: product.brand,
        item_variant: item.objectID,
        item_list_id: getNavigationId(findingMethod),
        item_list_name: getNavigationName(findingMethod),
        fulfillment_method: selectedDeliveryMethod,
        autoship_frequency: autoShipFrequency || '',
        store_id: store?.StoreNumber.toString(),
        size: (Object.values(product.size ?? {})[0] as string) ?? '',
        color: product.color?.value ?? '',
        flavor: product.flavor?.value ?? '',
        default_version: product.id === +item.objectID ? 'yes' : 'no',
        item_search_term: isFromSearch ? lastSearchQuery : null,
        item_cart_order: itemCartOrder,
    })) as EventItem[];
};

type AddToCartItems = {
    product: ProductHit;
    selectedProduct: MappedProductVariant;
    quantity?: number;
    store: StoreV2 | null | undefined;
    cartCount?: number;
    selectedDeliveryMethod: string;
    lastSearchQuery: string;
    frequency?: number;
};

export const mapAddToCartItems = ({
    product,
    selectedProduct,
    quantity,
    store,
    cartCount,
    selectedDeliveryMethod,
    lastSearchQuery,
    frequency,
}: AddToCartItems) => {
    const findingMethod = processFindingMethod(window.sessionStorage.pdpFindingMethod);
    const isFromSearch = findingMethod === TrackingParams.SEARCH_REFERER;

    return {
        item_name: selectedProduct.name ?? '',
        item_id: String(product.masterProductID?.toString()),
        quantity: quantity ?? 0,
        price: selectedProduct.price?.number ?? 0,
        ...getItemCategories(product),
        item_brand: selectedProduct.brand ?? '',
        item_variant: String(selectedProduct.id),
        item_list_id: getNavigationId(findingMethod),
        item_list_name: getNavigationName(findingMethod),
        fulfillment_method: selectedDeliveryMethod,
        autoship_frequency: frequency,
        store_id: store?.StoreNumber.toString(),
        color: selectedProduct.color?.value ?? '',
        size: (Object.values(selectedProduct.size ?? {})[0] as string) ?? '',
        flavor: selectedProduct.flavor?.value ?? '',
        default_version: selectedProduct.id === product.id ? 'yes' : 'no',
        item_search_term: isFromSearch ? lastSearchQuery : null,
        item_cart_order: cartCount,
    };
};

export const mapVelocityTrackCartItems = (
    itemsList: ItemsListItem[],
    addCartType?: string,
    isCrawl?: boolean
): TrackCartItem[] => {
    return itemsList.map(item => {
        const listAttribution = resolveGa4ItemListAttributionForCartItem(item);

        return {
            item_name: item.name ?? '',
            item_id: getGa4ItemId(item) ?? '',
            quantity: item.quantity ?? '',
            // This change is to fix the price issue for the Analitycs events, the price needs to be pushed without taxes
            price: toDollarsAmount(item.listPrice ?? 0),
            item_category: item.mhVicePresidentName ?? '',
            item_category2: item.mhDivision ?? '',
            item_category3: item.mhDepartment ?? '',
            item_category4: item.mhClass ?? '',
            item_category5: item.mhCategory ?? '',
            item_brand: item.merchandiseBrand ?? '',
            item_variant: item.sku,
            item_list_name: listAttribution.item_list_name,
            item_list_id: listAttribution.item_list_id,
            ...(listAttribution.index !== undefined && { index: listAttribution.index }),
            fulfillment_method: buildFulfillmentMethodLabel(item.fulfillmentMethod, item),
            autoship_frequency: formatAutoshipFrequency(item.autoshipFrequency, item.autoshipFrequencyUnitOfMeasure),
            store_id: item.storeId ?? null,
            color: item.variant?.color ?? '',
            size: item.variant?.size ?? '',
            flavor: item.variant?.flavor ?? '',
            default_version: item.masterProductKey,
            item_search_term: resolveItemSearchTerm(
                getSkuSearchTerm(item.sku),
                listAttribution.item_list_id === NavigationListTypes.SEARCH
                    ? (getSearchStats().searchTerm ?? null)
                    : null
            ),
            item_cart_order: item.positionWithinCart,
            ...(addCartType
                ? { add_to_cart_type: addCartType }
                : { add_to_cart_type: item.pliAddedInStore ? 'in store' : 'add' }),
            autoship_availability: item?.autoshipEnabled ? 'yes' : 'no',
            ...(isCrawl
                ? {
                      sth_availability: item?.sth_availability ?? '',
                      bopis_availability: item?.bopis_availability ?? '',
                      sdd_availability: item?.sdd_availability ?? '',
                      sth_stock_availability: item.sthStockAvailability ?? '',
                      bopis_stock_availability: item.bopisStockAvailability ?? '',
                      sdd_stock_availability: item.sddStockAvailability ?? '',
                  }
                : {
                      autoship_stock_availability: item.autoshipStockAvailability ?? '',
                  }),
            autoship_autoselected: 'no',
            salePrice: Number(item.salePrice ?? item.basePrice),
            scheduled_autoship_date: formatScheduledDeliveryDate(item.scheduledDeliveryDate),
        };
    });
};
